// File path : src\hooks\useImageViewer.ts

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Import the new API hook
import { useGetImageViewerDocumentByteQuery, useLazyGetSearchTextInFileQuery } from 'api/imageViewer';
import { CavseRefType, ImageViewerGetDocumentBytesResponse, ImageViwerNavigateFile } from 'types/image-viewer';
import { useDispatch } from 'react-redux';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { find, findIndex } from 'lodash';
import {
  useAddTablePatternMutation,
  useGetVerificationBatchDetailsQuery,
  useGetVerificationDocTypeFieldsQuery,
  useGetVerificationPatternListQuery,
  useUpdateTablePatternMutation,
  useVerificationAddNewPatternMutation,
  useVerificationDeletePatternMutation,
  useVerificationStatusUpdateMutation,
  useVerificationUpdatePatternMutation
} from 'api/verification';
import {
  AddTablePatternPayload,
  UpdateTablePatternPayload,
  VerifcaitonNewPatternPayloadType,
  VerifiactionFileDetailsTypes,
  VerifiationStatusTypes,
  VerificationPatternsListTypes,
  VerificationTechniquesEnum,
  VerificationTechniquesLabelEnum
} from 'types/verification';
import { generateMetricsFromROI } from 'utils/imageViewerUtils';

export const useVerificationImageViewer = () => {
  const canvasFunctionRef = useRef<CavseRefType>(null);
  const { batchId, fileId, docTypeId } = useParams();
  const navigate = useNavigate(); // For updating the URL
  const dispatch = useDispatch(); // For updating the URL
  const location = useLocation();
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentPageDetails, setCurrentPageDetails] = useState<ImageViewerGetDocumentBytesResponse>();
  const [selectedFieldId, setSelectedFieldId] = useState<number>(0);
  const [selectedTechniqueId, setSelectedTechniqueId] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<string>('-1');
  const [selectedPattern, setSelectedPattern] = useState<VerificationPatternsListTypes | null>(null);

  const { data: documentPageDetailsRes, isLoading: isLoadingImageBytes } = useGetImageViewerDocumentByteQuery(
    { fileID: Number(fileId), pageNo: currentPageNumber, batchID: Number(batchId) },
    { skip: !fileId || !batchId }
  );
  const [updateVerificationBatchStatus] = useVerificationStatusUpdateMutation();
  const [addNewPatternMutation] = useVerificationAddNewPatternMutation();
  const [updatePatternMutation] = useVerificationUpdatePatternMutation();
  const [deletePatternMutation] = useVerificationDeletePatternMutation();
  const [searchTextInFileQuery] = useLazyGetSearchTextInFileQuery();
  const [addTablePatternMutation] = useAddTablePatternMutation();
  const [updateTablePatternMutation] = useUpdateTablePatternMutation();

  const { data: fieldList, isLoading: isFieldListLoading } = useGetVerificationDocTypeFieldsQuery(
    { docTypeID: Number(docTypeId), batchID: Number(batchId) },
    { skip: !docTypeId || !batchId }
  );

  const { data: patternList, refetch: refetchPatternList } = useGetVerificationPatternListQuery(
    { docTypeID: Number(docTypeId) },
    { skip: !docTypeId }
  );
  const { data: batchList, refetch: refetchBatchList } = useGetVerificationBatchDetailsQuery(
    { docTypeID: Number(docTypeId || 0) },
    { skip: !docTypeId }
  );

  const fileLevelDetails: VerifiactionFileDetailsTypes | undefined = useMemo(() => {
    if (selectedPattern) {
      return {
        fileID: selectedPattern.fileID,
        batchID: selectedPattern.batchId,
        fileName: selectedPattern.fileName,
        docTypeId: selectedPattern.docTypeID,
        docType: selectedPattern.docType,
        totalPages: selectedPattern.totalPage,
        processID : selectedPattern?.processID
      };
    }
    return undefined;
  }, [selectedPattern]);

  const isVerifiedBatch: boolean = useMemo(() => {
    const batchDetails = find(batchList, { batchID: Number(batchId) });
    return batchDetails?.status === VerifiationStatusTypes.CURATION_COMPLETED;
  }, [batchId, batchList]);

  useEffect(() => {
    const batchDetails = find(batchList, { batchID: Number(batchId) });
    if (batchDetails && batchDetails?.status !== VerifiationStatusTypes.CURATION_COMPLETED) {
      (async () => {
        try {
          // updating to in progress
          await updateVerificationBatchStatus({ batchID: batchDetails.batchID }).unwrap();
        } catch (error) {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchList]);

  // Set current page details when document page details response is available
  useEffect(() => {
    if (documentPageDetailsRes) {
      setCurrentPageDetails(documentPageDetailsRes);
    }
  }, [documentPageDetailsRes]);

  // navigate to first first batch of the selected doc type
  useEffect(() => {
    if (batchList?.[0]?.batchID && !batchId) {
      navigate(`/review/image-viewer/verification/${docTypeId}/${batchList[0].batchID}`, {
        replace: true,
        state: location.state
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchList, batchId]);

  const searchTextInFile = (searchText: string): Promise<any[]> =>
    new Promise(async (resolve, reject) => {
      try {
        const { data }: { data?: unknown } = await searchTextInFileQuery({ fileID: Number(fileId || 0), searchText });
        if (data) {
          resolve(data as any[]);
        } else {
          resolve([]);
        }
      } catch (e) {
        reject(e);
      }
    });

  const handleBatchChange: any = useCallback(
    (batchID: string) => {
      navigate(`/review/image-viewer/verification/${docTypeId}/${batchID}`, {
        replace: true,
        state: location.state
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.state, docTypeId]
  );

  const handleFileNavigation: ImageViwerNavigateFile = useCallback(
    (direction) => async () => {
      try {
        if (!batchList) return;
        await refetchBatchList().unwrap();
        const currentFileIndex = findIndex(batchList, { batchID: Number(batchId) });
        const nextBatchDetails = batchList[currentFileIndex + (direction === 'NEXT' ? 1 : -1)];
        if (nextBatchDetails) {
          navigate(`/review/image-viewer/verification/${docTypeId}/${nextBatchDetails.batchID}`, {
            replace: true,
            state: location.state
          });
          setSelectedPattern(null);
        } else {
          navigate('/review/verification', { replace: true });
        }
      } catch (error) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batchList, batchId, location.state, docTypeId, fileId]
  );

  const onClickBack = useCallback(async () => {
    try {
      // updating to in progress
      const batchDetails = find(batchList, { batchID: Number(batchId) });
      if (batchDetails && batchDetails?.status !== VerifiationStatusTypes.CURATION_COMPLETED && !isVerifiedBatch) {
        await updateVerificationBatchStatus({ batchID: batchDetails.batchID, isBack: true }).unwrap();
      }
      navigate('/review/verification', { replace: true });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, batchList, isVerifiedBatch]);

  const finishAndMoveNextBatch = (batchID: number, docType: string) =>
    new Promise(async (resolve, reject) => {
      try {
        await updateVerificationBatchStatus({ batchID, isFinish: true }).unwrap();
        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: `Document type '${docType}' batch ID '${batchID}' verified successfully`,
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        handleFileNavigation('NEXT')();
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });

  const handleAddNewPattern = (data: any) =>
    new Promise(async (resolve, reject) => {
      try {
        const isInsert = data.mode === 'ADD';
        const fieldDetails = fieldList?.find(({ fieldID }) => fieldID === selectedFieldId);
        if (!fieldDetails || !selectedPattern) return;
        const techniqueDetails = fieldDetails.techniques.find(({ techID }) => techID === selectedTechniqueId);
        if (!techniqueDetails) return;

        const firstPart = techniqueDetails.technique
          ? techniqueDetails.technique.includes('Nested Lookup Printed')
            ? 'NestedLookupPrinted'
            : techniqueDetails.technique.includes('Nested - Keyvaluepair printed')
            ? 'NestedKeyvaluepairprinted'
            : techniqueDetails.technique.toLowerCase().includes('lookup printed')
            ? 'Lookup'
            : techniqueDetails.technique.toUpperCase().includes('KEYVALUEPAIR')
            ? 'KeyValuePair'
            : techniqueDetails.technique.split('-')[0]?.trim()
          : '';
        console.log(firstPart);
        if (String(firstPart).trim() === VerificationTechniquesLabelEnum[VerificationTechniquesEnum.TABLE_PRINTED]) {
          if (isInsert) {
            const payload: AddTablePatternPayload = {
              taxonomyID: 0,
              columnID: selectedPattern.columnID,
              patternValue: data.documentPattern,
              patternROI: data.patternROI,
              patternPageNo: selectedPattern.pageNumber,
              fileId: selectedPattern.fileID,
              fieldId: selectedPattern.fieldID,
              techId: selectedPattern.techID
            };
            await addTablePatternMutation(payload).unwrap();
          } else {
            const payload: UpdateTablePatternPayload = {
              taxonomyID: selectedPattern?.taxonomyID || 0,
              columnID: selectedPattern.columnID,
              patternValue: data.documentPattern,
              patternROI: data.patternROI,
              patternPageNo: selectedPattern.pageNumber
            };
            await updateTablePatternMutation(payload).unwrap();
          }
        } else {
          let payload: VerifcaitonNewPatternPayloadType = {
            fileId: Number(fileId),
            fieldId: selectedFieldId,
            techId: selectedTechniqueId,
            taxonomyID: isInsert ? 0 : selectedPattern?.taxonomyID || 0,
            isKeyValuePair:
              firstPart === 'NestedKeyvaluepairprinted'
                ? true
                : Number(techniqueDetails.componentID) === 23
                ? false
                : firstPart.includes('-')
                ? firstPart.split('-')[0].trim() === VerificationTechniquesLabelEnum[VerificationTechniquesEnum.KEY_VALUE_PAIR]
                : firstPart.trim() === VerificationTechniquesLabelEnum[VerificationTechniquesEnum.KEY_VALUE_PAIR],

            patternValue:
              Number(techniqueDetails.componentID) === 23
                ? data.patternText
                : data.sectionHeader
                ? data.sectionHeader
                : data.patternText || '',
            IsContextCloud: Number(techniqueDetails.componentID) === 23 ? true : false,
            patternROI: data.patternROI,
            patternPageNo: currentPageNumber,
            contentDirectionID: 5,
            sectionHeader: data.sectionHeader,
            sectionFooter: data.sectionFooter,
            isExpression: Boolean(data.expressionPattern),
            expressionPattern: data.expressionPattern || '',
            expressionRegex: data.expressionRegex || '',
            isLookup: firstPart === 'NestedLookupPrinted' ? true : Boolean(data.documentPattern),
            documentPattern:
              firstPart === 'NestedLookupPrinted'
                ? data.sectionHeader
                  ? data.sectionHeader
                  : data.documentPattern
                : data.documentPattern || '',
            displayValue: data.displayValue
              ? data.displayValue
              : firstPart === 'NestedLookupPrinted'
              ? data.sectionHeader
                ? data.sectionHeader
                : data.documentPattern
              : data.documentPattern
          };
          if (isInsert) {
            await addNewPatternMutation(payload).unwrap();
          } else {
            await updatePatternMutation(payload).unwrap();
          }
        }
        await refetchPatternList().unwrap();
        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: `'${fieldDetails?.fieldName}' pattern ${isInsert ? 'added' : 'updated'} successfully`,
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });

  const deletePattern: any = (taxonomyID: number) =>
    new Promise(async (resolve, reject) => {
      try {
        await deletePatternMutation({ taxonomyID }).unwrap();
        const fieldDetails = fieldList?.find(({ fieldID }) => fieldID === selectedFieldId);
        dispatch(
          openDefaultSuccessSnackbar({
            open: true,
            message: `'${fieldDetails?.fieldName}' pattern deleted successfully`,
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        await refetchPatternList().unwrap();
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });

  const handleSelectPattern = (data: VerificationPatternsListTypes | null, isDualScreen?: boolean) => {
    if (data && docTypeId) {
      if (!isDualScreen) {
        navigate(`/review/image-viewer/verification/${docTypeId}/${data.batchId}/${data.fileID}`, {
          replace: true,
          state: location.state
        });
      } else {
        navigate(`/verification-image-viewer-secondary-view/${docTypeId}/${data.batchId}/${data.fileID}?dualScreen=true`, {
          replace: true,
          state: location.state
        });
      }
      setCurrentPageNumber(data.pageNumber);
    } else {
      if (canvasFunctionRef?.current) {
        canvasFunctionRef.current.highlightRubberBand(null); // To clear previously selected
      }
    }
    setSelectedPattern(data);
  };

  // To work with differnt files
  useEffect(() => {
    if (fileId && selectedPattern && Number(fileId) === selectedPattern?.fileID && documentPageDetailsRes?.pageBytes) {
      setTimeout(() => {
        if (canvasFunctionRef?.current) {
          const metrics = generateMetricsFromROI(selectedPattern.patternROI);
          canvasFunctionRef.current.highlightRubberBand({
            left: metrics?.left || 0,
            top: metrics?.top || 0,
            width: metrics?.width || 0,
            height: metrics?.height || 0,
            pageNo: selectedPattern.pageNumber
          });
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, selectedPattern, canvasFunctionRef, documentPageDetailsRes?.pageBytes]);

  const isTableTechnique: boolean = useMemo(() => {
    if (!fieldList) return false;
    const fieldDetails = fieldList?.find(({ fieldID }) => fieldID === selectedFieldId);
    if (!fieldDetails || !selectedPattern) return false;
    const techniqueDetails = fieldDetails.techniques.find(({ techID }) => techID === selectedTechniqueId);
    if (!techniqueDetails) return false;
    return String(techniqueDetails.technique).trim() === VerificationTechniquesLabelEnum[VerificationTechniquesEnum.TABLE_PRINTED];
  }, [fieldList, selectedFieldId, selectedPattern, selectedTechniqueId]);

  return {
    fileId,
    currentPageDetails: selectedPattern ? currentPageDetails : undefined,
    currentPageNumber,
    setCurrentPageNumber,
    isLoadingImageBytes,
    fileLevelDetails,
    searchTextInFile,
    handleFileNavigation,
    canvasFunctionRef,
    fieldList,
    isFieldListLoading,
    selectedFieldId,
    setSelectedFieldId,
    selectedTechniqueId,
    setSelectedTechniqueId,
    patternList: patternList || [],
    batchList: batchList || [],
    selectedUser,
    setSelectedUser,
    batchId,
    finishAndMoveNextBatch,
    handleBatchChange,
    handleAddNewPattern,
    deletePattern,
    selectedPattern,
    setSelectedPattern: handleSelectPattern,
    onClickBack,
    isVerifiedBatch,
    isTableTechnique
  };
};
